import { useMemo } from 'react';
import { PaymentsFeatureFlags } from '@frontend/payments-hooks';
import { useAppScopeStore } from '@frontend/scope';
import { useAppFlagStore } from '@frontend/shared';
import { PaymentsTableInstances } from '../../utils/helpers';
import { GenerateColumnsFn, useGenerateColumns } from '../use-generate-columns';

type GenerateColumnsHOF<T extends NonNullable<unknown>> = (options: {
  isMulti: boolean;
  showPayoutDescriptor: boolean;
  hasSurchargesEnabled: boolean;
}) => GenerateColumnsFn<T>;

export const useColumnsWithPayoutDescriptor = <T extends NonNullable<unknown>>({
  forPrint,
  hideLocationNameColumn = false,
  generateColumns,
  tableName,
  hasSurchargesEnabled,
}: {
  forPrint: boolean;
  hideLocationNameColumn?: boolean;
  generateColumns: GenerateColumnsHOF<T>;
  tableName: PaymentsTableInstances;
  hasSurchargesEnabled?: boolean;
}) => {
  const { selectedLocationIds: locationIds } = useAppScopeStore();

  const { getFeatureFlagValue } = useAppFlagStore();
  const showPayoutDescriptor = getFeatureFlagValue({
    flagName: PaymentsFeatureFlags.PayoutDescriptorColumn,
    locationIds: locationIds,
  });
  const generateTableColumns = useMemo(
    () =>
      generateColumns({
        showPayoutDescriptor,
        hasSurchargesEnabled: hasSurchargesEnabled ?? false,
        isMulti: locationIds.length > 1,
      }),
    [generateColumns, showPayoutDescriptor, hasSurchargesEnabled, locationIds.length]
  );

  const columns = useGenerateColumns<T>(generateTableColumns, forPrint, tableName, {
    hideLocationNameColumn,
  });

  return { columns, showPayoutDescriptor };
};
